import times from 'lodash/times';
import type { ReactElement } from 'react';
import styles from './Rating.module.scss';
import FontAwesomeIcon, {
  solidStar,
  regularStar,
  regularStarHalfStroke
} from 'common/components/FontAwesomeIcon';

type Props = {
  score?: number;
  max?: number;
};

function partialStar(fraction: number): ReactElement | null {
  if (fraction === 0) {
    return null;
  } else if (fraction <= 0.25) {
    return (
      <FontAwesomeIcon
        icon={regularStar}
        className={styles.emptyStar}
        aria-hidden={false}
        aria-label={__('shared.stars.empty')}
      />
    );
  } else if (fraction <= 0.75) {
    return (
      <FontAwesomeIcon
        icon={regularStarHalfStroke}
        className={styles.star}
        aria-hidden={false}
        aria-label={__('shared.stars.half')}
      />
    );
  } else {
    return (
      <FontAwesomeIcon
        icon={solidStar}
        className={styles.star}
        aria-hidden={false}
        aria-label={__('shared.stars.full')}
      />
    );
  }
}

export function Rating({ score = 0, max = 5 }: Props): ReactElement {
  const fraction = score - Math.floor(score);
  const fullStarCount = Math.floor(score);
  const emptyStarCount = max - Math.ceil(score);

  return (
    <div className={styles.rating} data-testid="rating">
      {times(fullStarCount, index => (
        <FontAwesomeIcon
          icon={solidStar}
          className={styles.star}
          aria-hidden={false}
          aria-label={__('shared.stars.full')}
          key={`star-full:${index}`}
        />
      ))}
      {partialStar(fraction)}
      {times(emptyStarCount, index => (
        <FontAwesomeIcon
          icon={regularStar}
          className={styles.emptyStar}
          aria-hidden={false}
          aria-label={__('shared.stars.empty')}
          key={`star-empty:${index}`}
        />
      ))}
    </div>
  );
}
